import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../components/api';

export const useEnseignant = () => {
  const [enseignants, setEnseignants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    count: 0,
    total_pages: 0,
    current_page: 1,
    page_size: 10,
    has_next: false,
    has_previous: false
  });

  // Récupérer la liste des enseignants avec pagination
  const fetchEnseignants = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '10'
      });
      
      if (search) {
        params.append('search', search);
      }
      
      const response = await api.get(`/enseignant/liste-enseignant/?${params}`);
      
      if (response.data.status === 'success') {
        setEnseignants(response.data.data || []);
        setPagination(response.data.pagination || {});
      } else {
        setEnseignants([]);
        setPagination({
          count: 0,
          total_pages: 0,
          current_page: 1,
          page_size: 10,
          has_next: false,
          has_previous: false
        });
      }
    } catch (error) {
      console.error("Erreur lors du chargement des enseignants:", error);
      toast.error("Impossible de charger la liste des enseignants");
      setEnseignants([]);
    } finally {
      setLoading(false);
    }
  };

  // Gérer la recherche avec pagination
  const handleSearch = (term) => {
    setSearchTerm(term);
    fetchEnseignants(1, term);
  };

  // Changer de page
  const handlePageChange = (page) => {
    fetchEnseignants(page, searchTerm);
  };

  // Créer un enseignant
  const createEnseignant = async (formData) => {
    setSubmitting(true);
    try {
      const dataToSend = {
        ...formData,
        status_id: formData.status ? Number(formData.status) : null
      };

      // Supprimer le champ status si présent pour éviter la confusion
      if ('status' in dataToSend) {
        delete dataToSend.status;
      }

      const response = await api.post('/enseignant/liste-enseignant/', dataToSend);
      
      // Recharger les enseignants après création
      await fetchEnseignants(pagination.current_page, searchTerm);
      
      toast.success("Enseignant créé avec succès!");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Erreur lors de la création de l'enseignant:", error);
      
      if (error.response?.data?.email) {
        toast.error("Un enseignant avec cette adresse email existe déjà");
      } else if (error.response?.data?.matricule) {
        toast.error("Un enseignant avec ce matricule existe déjà");
      } else {
        toast.error("Impossible de créer l'enseignant");
      }
      
      return { success: false, error: error.response?.data };
    } finally {
      setSubmitting(false);
    }
  };

  // Modifier un enseignant
  const updateEnseignant = async (id, formData) => {
    setSubmitting(true);
    try {
      const dataToSend = {
        ...formData,
        status_id: formData.status ? Number(formData.status) : null
      };

      // Supprimer le champ status si présent pour éviter la confusion
      if ('status' in dataToSend) {
        delete dataToSend.status;
      }

      const response = await api.put(`/enseignant/details-enseignant/${id}/`, dataToSend);
      
      // Recharger les enseignants après modification
      await fetchEnseignants(pagination.current_page, searchTerm);
      
      toast.success("Enseignant modifié avec succès!");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Erreur lors de la modification de l'enseignant:", error);
      
      if (error.response?.data?.email) {
        toast.error("Un enseignant avec cette adresse email existe déjà");
      } else if (error.response?.data?.matricule) {
        toast.error("Un enseignant avec ce matricule existe déjà");
      } else {
        toast.error("Impossible de modifier l'enseignant");
      }
      
      return { success: false, error: error.response?.data };
    } finally {
      setSubmitting(false);
    }
  };

  // Supprimer un enseignant
  const deleteEnseignant = async (id) => {
    setDeleteLoading(true);
    try {
      await api.delete(`/enseignant/details-enseignant/${id}/`);
      
      // Recharger les enseignants après suppression
      await fetchEnseignants(pagination.current_page, searchTerm);
      
      toast.success("Enseignant supprimé avec succès!");
      return { success: true };
    } catch (error) {
      console.error("Erreur lors de la suppression de l'enseignant:", error);
      
      if (error.response?.status === 403) {
        toast.error("Vous n'avez pas la permission de supprimer cet enseignant");
      } else {
        toast.error("Impossible de supprimer l'enseignant");
      }
      
      return { success: false, error: error.response?.data };
    } finally {
      setDeleteLoading(false);
    }
  };

  // Charger les enseignants au montage du composant
  useEffect(() => {
    fetchEnseignants();
  }, []);

  return {
    enseignants,
    loading,
    submitting,
    deleteLoading,
    searchTerm,
    pagination,
    handleSearch,
    handlePageChange,
    createEnseignant,
    updateEnseignant,
    deleteEnseignant,
    fetchEnseignants
  };
}; 